import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { isSafeMode } from '../lib/safeMode';
import { ShortsPlayer } from '../components/ShortsPlayer';
import { AdSlot } from '../components/AdSlot';

const PAGE_SIZE = 12;
const MUTE_KEY = 'alb_video_muted';

export default function ShortsPage() {
    const [params] = useSearchParams();
    const focusId = params.get('id');
    const [items, setItems] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);
    // Shared mute preference so toggling audio on one Short applies to every
    // Short in the feed and persists across scroll transitions.
    const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) !== 'false');
    const containerRef = useRef(null);
    const nextPageRef = useRef(0);
    const loadingRef = useRef(false);
    const hasMoreRef = useRef(true);

    const toggleMute = () => {
        setMuted((prev) => {
            const next = !prev;
            localStorage.setItem(MUTE_KEY, String(next));
            return next;
        });
    };

    useEffect(() => {
        (async () => {
            nextPageRef.current = 0;
            hasMoreRef.current = true;
            loadingRef.current = true;
            try {
                const r = await api.get('/shorts/feed', { params: { page: 0, limit: PAGE_SIZE } });
                let arr = r.data.items || [];
                if (isSafeMode()) arr = arr.filter((v) => !v.is_adult);
                if (focusId) {
                    const idx = arr.findIndex((v) => v.video_id === focusId);
                    if (idx > 0) { arr = [arr[idx], ...arr.slice(0, idx), ...arr.slice(idx + 1)]; }
                }
                setItems(arr);
                nextPageRef.current = 1;
                hasMoreRef.current = !!r.data.has_more;
            } finally {
                loadingRef.current = false;
            }
        })();
    }, [focusId]);

    const loadMore = async () => {
        if (loadingRef.current || !hasMoreRef.current) return;
        loadingRef.current = true;
        try {
            const page = nextPageRef.current;
            const response = await api.get('/shorts/feed', { params: { page, limit: PAGE_SIZE } });
            let next = response.data.items || [];
            if (isSafeMode()) next = next.filter((v) => !v.is_adult);
            nextPageRef.current = page + 1;
            hasMoreRef.current = !!response.data.has_more;
            if (next.length) setItems((current) => [...current, ...next.filter((item) => !current.some((old) => old.video_id === item.video_id))]);
        } catch {
            // Allow a retry on the next scroll tick instead of getting stuck.
        } finally {
            loadingRef.current = false;
        }
    };

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onScroll = () => {
            const h = el.clientHeight;
            const idx = Math.round(el.scrollTop / h);
            setActiveIdx(idx);
            if (idx >= items.length - 3) loadMore();
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, [items.length]);

    return (
        <div data-testid="shorts-feed" className="fixed inset-0 top-14 md:top-16 bottom-16 md:bottom-0 bg-black">
            {items.length === 0 ? (
                <div className="h-full grid place-items-center text-[color:var(--alb-text-muted)]">
                    <div className="text-center">
                        <div className="font-display text-2xl mb-2">Encara no hi ha Shorts</div>
                        <div>Puja un vídeo curt per estrenar la secció.</div>
                    </div>
                </div>
            ) : (
                <div ref={containerRef} className="h-full overflow-y-scroll snap-y no-scrollbar">
                    {items.map((v, i) => (
                        <div key={v.video_id} className="h-full snap-center">
                            <ShortsPlayer video={v} active={i === activeIdx} muted={muted} onToggleMute={toggleMute} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
